import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';

import setField from 'webapp/helpers/set-field';
import CommonPopupsColorPickerComponent from 'webapp/components/common/popups/color-picker';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';

import type PopupContext from 'webapp/models-custom/popup/context';

interface Args {
	value: string;
	isDisabled?: boolean;
	colorIcon?: string;
	hasSubmit?: boolean;
	theme?: string;
	onChange: (value: string) => void;
}

const COLOR_ICON_MAP: Record<string, IconName> = {
	raindrop: 'RaindropFill',
};

export default class FormColorPickerComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;

	hasHash = false;

	popup: PopupContext | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.hasHash = this.args.value?.startsWith('#');
	}

	get colorIconName(): IconName | null {
		const colorIcon = this.args.colorIcon;
		return colorIcon ? COLOR_ICON_MAP[colorIcon] || null : null;
	}

	get colorStyle(): SafeString {
		const colorStyle = `${this.args.colorIcon ? '' : 'background-'}color:#${this.inputShownValue}`;

		return htmlSafe(colorStyle);
	}

	get inputShownValue(): string {
		const value = this.args.value;

		return this.hasHash ? value.substr(1) : value;
	}

	set inputShownValue(value: string) {
		if (this.popup) {
			this.popup.triggerEvent('outside-color-change', this.getFormattedValue(value));
		}
		this.change(value);
	}

	get theme() {
		return this.args.theme || 'light';
	}

	@action
	change(value: string): void {
		const formattedValue = this.getFormattedValue(value);
		this.args.onChange(formattedValue);
	}

	@action
	scheduleOpenColorPicker(e: FocusEvent) {
		if (this.args.isDisabled) {
			return;
		}

		const attachTo = (e.target as HTMLElement)?.closest<HTMLElement>('.colorpicker');
		if (!attachTo) {
			return;
		}

		later(() => {
			this.openColorPicker(attachTo);
		}, 100);
	}

	openColorPicker(attachTo: HTMLElement): void {
		if (this.popupService.isOpen(CommonPopupsColorPickerComponent)) {
			return;
		}

		const popup = this.popupService.createContext(CommonPopupsColorPickerComponent, attachTo, {
			popupClass: 'popup-type-color-picker',
			theme: this.args.theme,
			autofocus: false,
		});

		popup.open({
			value: this.inputShownValue,
			onChange: this.change,
		});

		this.popup = popup;

		popup.on(PopupEvent.CLOSING, this, () => {
			this.popup = null;
		});
	}

	private getFormattedValue(value: string) {
		return this.hasHash ? `#${value}` : value;
	}
	<template>
		<div class="colorpicker {{if @isDisabled 'colorpicker--disabled'}}">

			<FormInputComponent
				maxlength="6"
				disabled={{@isDisabled}}
				@value={{this.inputShownValue}}
				@focusIn={{this.scheduleOpenColorPicker}}
				@onEmpty={{setField target=this property="inputShownValue"}}
				@hideFloatLabel={{true}}
			/>
			{{#if this.colorIconName}}
				<span
					role="button"
					class="color"
					style={{this.colorStyle}}
					{{on "click" this.scheduleOpenColorPicker}}
				>
					<CommonUiIconComponent @name={{this.colorIconName}} @size="sm" />
				</span>
			{{else}}
				<span
					role="button"
					class="rect"
					style={{this.colorStyle}}
					{{on "click" this.scheduleOpenColorPicker}}
				></span>
			{{/if}}

		</div>
	</template>
}
